import React from 'react';
import { Printer } from 'react-feather';
import PageLayout from '../../../components/PageLayout';
import {
  formatDate,
  getItemById,
  getQueryString,
} from '../../../utils/common.util';
import useSWR from 'swr';
import {
  DatePicker,
  DatePickerInput,
  Select,
  SelectItem,
} from 'carbon-components-react';
import PrintOutHead from '../../../components/PrintOutHead';
import { PrintView } from '../../../components/PrintView/print-view.component';
import CustomButton from '../../../components/custom-button/custom-btn.component';
import { useUrlState } from '../../../utils/useUrlState';
import useCurrentAcademicTerm from '../../../utils/useCurrentAcademicTerm';
import './students-attendance.styles.scss';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

const initialData = {
  academicYearId: '',
  termId: '',
  fromDate: '',
  toDate: '',
};

export default function AttendanceSummaryReport() {
  const pageTitle = 'Attendance Summary Report';
  const [query, setQuery] = useUrlState(initialData);

  useCurrentAcademicTerm({
    academicYearId: query.academicYearId,
    setQuery,
  });

  const { data: academicYears } = useSWR(
    'academic-year/list/?skip=0&take=100&query=',
  );
  const { data: terms } = useSWR(
    query.academicYearId
      ? `academic-year/terms/?academicYearId=${query.academicYearId}`
      : null,
  );

  // if date is to be provided, both fromdate and todate must be provided. the two must be provided
  const isDateProvided = query.fromDate && query.toDate;
  const { data, isLoading, error } = useSWR(
    isDateProvided || query.academicYearId
      ? `students-attendance/reports?${getQueryString(query)}`
      : null,
  );

  const history = useHistory();

  const renderTableBody = () => {
    const dataRows = data?.classes.map((row, idx) => (
      <tr key={row.classDescription + idx}>
        <td>{row.classDescription}</td>
        <td>{row.maleStudents.present}</td>
        <td>{row.maleStudents.absent}</td>
        <td>{row.maleStudents.late}</td>
        <td>{row.maleStudents.excused}</td>
        <td>{row.femaleStudents.present}</td>
        <td>{row.femaleStudents.absent}</td>
        <td>{row.femaleStudents.late}</td>
        <td>{row.femaleStudents.excused}</td>
        <td>{row.total.present}</td>
        <td>{row.total.absent}</td>
        <td>{row.total.late}</td>
        <td>{row.total.excused}</td>
        <td className="font-bold">{row.overall}</td>
        <td>
          <button
            className="clean-btn link"
            onClick={() =>
              history.push(
                `/students/attendance/student-summary?${getQueryString({
                  ...query,
                  classDescription: row.classDescription,
                })}`,
              )
            }
          >
            View
          </button>
        </td>
      </tr>
    ));

    // Render the total row if available
    const total = data.overAllTotal || {};
    const hasTotal = Object.keys(total).length > 0;

    return (
      <tbody>
        {dataRows}
        {hasTotal && (
          <tr className="font-bold">
            <td>{total.classDescription}</td>
            <td>{total.maleStudents.present}</td>
            <td>{total.maleStudents.absent}</td>
            <td>{total.maleStudents.late}</td>
            <td>{total.maleStudents.excused}</td>
            <td>{total.femaleStudents.present}</td>
            <td>{total.femaleStudents.absent}</td>
            <td>{total.femaleStudents.late}</td>
            <td>{total.femaleStudents.excused}</td>
            <td>{total.total.present}</td>
            <td>{total.total.absent}</td>
            <td>{total.total.late}</td>
            <td>{total.total.excused}</td>
            <td>{total.overall}</td>
            <td></td>
          </tr>
        )}
      </tbody>
    );
  };

  return (
    <PageLayout
      breadcrumbs={[
        { label: 'Academics', to: '/academic' },
        { label: 'Attendance', to: '/students/attendance' },
        { label: pageTitle, active: true },
      ]}
      title={pageTitle}
      isLoading={isLoading}
    >
      {/* FILTERS  */}
      <div className="mt-10 d-flex align-items-center gap-20">
        <DatePicker
          dateFormat="Y-m-d"
          datePickerType="single"
          id="start-date-picker"
          maxDate={new Date().toISOString().split('T')[0]}
          onChange={(value) => {
            if (value.length > 0) {
              setQuery({
                fromDate: [
                  value[0].getFullYear(),
                  ('0' + (value[0].getMonth() + 1)).slice(-2),
                  ('0' + value[0].getDate()).slice(-2),
                ].join('-'),
                academicYearId: '',
                termId: '',
                toDate: '',
              });
            } else setQuery({ fromDate: '' });
          }}
          onClose={(value) => {}}
        >
          <DatePickerInput
            className="start-date"
            id="start-date"
            name="fromDate"
            labelText="Start Date"
            value={query.fromDate}
            max={new Date().toISOString().split('T')[0]}
            // pattern="d{1,2}/d{4}"
            pattern="\d{4}-\d{2}-\d{2}"
            autoComplete="off"
            placeholder="YYYY-MM-DD"
            type="text"
            onChange={(e) =>
              setQuery({
                fromDate: e.target.value,
                academicYearId: '',
                termId: '',
                toDate: '',
              })
            }
          />
        </DatePicker>
        <DatePicker
          dateFormat="Y-m-d"
          datePickerType="single"
          id="end-date-picker"
          // minDate={
          //   new Date(query.fromDate ?? '').toISOString().split('T')[0]
          // }
          maxDate={new Date().toISOString().split('T')[0]}
          onChange={(value) => {
            if (value.length > 0) {
              setQuery({
                toDate: [
                  value[0].getFullYear(),
                  ('0' + (value[0].getMonth() + 1)).slice(-2),
                  ('0' + value[0].getDate()).slice(-2),
                ].join('-'),
                academicYearId: '',
                termId: '',
              });
            } else setQuery({ toDate: '' });
          }}
          onClose={(value) => {}}
        >
          <DatePickerInput
            className="end-date"
            id="end-date"
            name="toDate"
            labelText="End Date"
            value={query.toDate}
            max={new Date().toISOString().split('T')[0]}
            // pattern="d{1,2}/d{4}"
            pattern="\d{4}-\d{2}-\d{2}"
            autoComplete="off"
            placeholder="YYYY-MM-DD"
            type="text"
            onChange={(e) =>
              setQuery({
                toDate: e.target.value,
                academicYearId: '',
                termId: '',
              })
            }
          />
        </DatePicker>
        <Select
          labelText="Academic Year"
          onChange={(e) =>
            setQuery({
              academicYearId: e.target.value,
              termId: '',
              fromDate: '',
              toDate: '',
            })
          }
          value={query.academicYearId}
        >
          <SelectItem value="" text="All" />
          {academicYears?.map((year) => (
            <SelectItem key={year.id} value={year.id} text={year.description} />
          ))}
        </Select>
        <Select
          labelText="Term"
          disabled={!query.academicYearId}
          onChange={(e) => setQuery({ termId: e.target.value })}
          value={query.termId}
        >
          <SelectItem value="" text="All" />
          {terms?.map((term) => (
            <SelectItem key={term.id} value={term.id} text={term.description} />
          ))}
        </Select>
      </div>

      <div className="mt-20">
        {error && <p className="text-14 mt-10 text-danger">{error.message}</p>}
        {data?.classes?.length > 0 && (
          <>
            <PrintOutHead
              title={pageTitle}
              headings={[
                {
                  label: 'Academic Year',
                  value:
                    getItemById(academicYears, query.academicYearId)
                      ?.description ?? 'All',
                },
                {
                  label: 'Term',
                  value: getItemById(terms, query.termId)?.description ?? 'All',
                },
                {
                  label: 'Date Range',
                  value: query.fromDate
                    ? `${formatDate(query.fromDate)} - ${formatDate(
                        query.toDate,
                      )} `
                    : '-',
                },
              ]}
            />
            <PrintView>
              <div style={{ overflow: 'auto' }}>
                <table className="bx--data-table  summary-table">
                  <thead>
                    <tr className="first-header">
                      <th rowSpan="2">Class</th>
                      <th colSpan="4">Male</th>
                      <th colSpan="4">Female</th>
                      <th colSpan="4">Total</th>
                      <th rowSpan="2">Overall</th>
                      <th rowSpan="2">Action</th>
                    </tr>
                    <tr className="second-header">
                      <th>Present</th>
                      <th>Absent</th>
                      <th>Late</th>
                      <th>Excused</th>
                      <th>Present</th>
                      <th>Absent</th>
                      <th>Late</th>
                      <th>Excused</th>
                      <th>Present</th>
                      <th>Absent</th>
                      <th>Late</th>
                      <th>Excused</th>
                    </tr>
                  </thead>
                  {renderTableBody()}
                </table>
              </div>
            </PrintView>
          </>
        )}
        {data?.length === 0 && (
          <p className="text-14 mt-30 text-grey">No data available.</p>
        )}

        {data?.classes?.length > 0 && (
          <div
            className="mt-30"
            style={{
              display: 'flex',
              justifySelf: 'end',
              gap: '20px',
              alignItems: 'center',
            }}
          >
            <CustomButton classes="secondary" onClick={() => window.print()}>
              <Printer size="16" strokeWidth="2px" /> &nbsp; Print
            </CustomButton>
          </div>
        )}
      </div>
    </PageLayout>
  );
}
